// Shard cache — ETag-based fetch wrapper for KG shards (WASM + TTL + SHACL).
//
// Design (WASM doc §7):
//   - Each shard is served with strong ETag = content hash.
//   - Browser HTTP cache handles the common case (Cache-Control: immutable).
//   - This module provides a thin helper that:
//       1. Optionally reads from a CacheStorage instance if one is registered
//          by the host page (SW stubs are a Phase 2a concern — we don't
//          register anything here).
//       2. Falls back to fetch().
//       3. Exposes an `invalidate()` hook for future BroadcastChannel-driven
//          invalidation.
//
// Sub-step 6 scope: the cache is a passthrough. The API shape is locked so
// Studio/Starter/Commerce integration (Steps 4-6) can swap in real caching
// without breaking callers.

export interface ShardFetchOpts {
  /** URL (absolute or relative) of the shard. */
  url: string | URL
  /**
   * Caller-supplied expected ETag / content-hash (defense in depth; caller
   * typically pins this to the build metadata). If provided + the server
   * returns a different etag, the fetch is treated as a failure.
   */
  expectedEtag?: string
  /** AbortSignal for cancellation (e.g. React unmount). */
  signal?: AbortSignal
  /**
   * Name of the CacheStorage bucket to consult. If absent, go straight to
   * network. If present + miss, we populate the bucket after network fetch.
   * (Phase 2a SW will register `cogo-kg-v<N>` buckets — for now callers don't
   * need to set this.)
   */
  cacheBucket?: string
}

export interface ShardFetchResult {
  body: ArrayBuffer
  etag: string | null
  fromCache: boolean
  bytes: number
}

/**
 * Fetch a shard. Returns the ArrayBuffer body + ETag + cache-hit flag.
 * Throws on network error or etag mismatch.
 */
export async function fetchShard(opts: ShardFetchOpts): Promise<ShardFetchResult> {
  const urlStr = typeof opts.url === "string" ? opts.url : opts.url.toString()

  // Cache lookup (if bucket requested + CacheStorage available)
  if (opts.cacheBucket && typeof caches !== "undefined") {
    try {
      const cache = await caches.open(opts.cacheBucket)
      const match = await cache.match(urlStr)
      if (match) {
        const buf = await match.arrayBuffer()
        const etag = match.headers.get("etag")
        if (opts.expectedEtag && etag && etag !== opts.expectedEtag) {
          // Cached but stale — fall through to network.
        } else {
          return { body: buf, etag, fromCache: true, bytes: buf.byteLength }
        }
      }
    } catch {
      // CacheStorage access failed (private browsing, etc.) — fall through.
    }
  }

  const res = await fetch(urlStr, { signal: opts.signal })
  if (!res.ok) {
    throw new Error(
      `[cogo-kg-browser] shard fetch failed ${res.status} ${res.statusText} — ${urlStr}`,
    )
  }
  const etag = res.headers.get("etag")
  if (opts.expectedEtag && etag && etag !== opts.expectedEtag) {
    throw new Error(
      `[cogo-kg-browser] shard etag mismatch — expected ${opts.expectedEtag}, got ${etag}`,
    )
  }
  const clone = res.clone()
  const buf = await res.arrayBuffer()

  // Opportunistic cache fill.
  if (opts.cacheBucket && typeof caches !== "undefined") {
    try {
      const cache = await caches.open(opts.cacheBucket)
      await cache.put(urlStr, clone)
    } catch {
      // Non-fatal; caller still gets the body.
    }
  }

  return { body: buf, etag, fromCache: false, bytes: buf.byteLength }
}

/**
 * Invalidate a shard URL from the named bucket. Invoked by the future
 * BroadcastChannel handler + Realtime invalidation plumbing.
 */
export async function invalidateShard(cacheBucket: string, url: string): Promise<boolean> {
  if (typeof caches === "undefined") return false
  try {
    const cache = await caches.open(cacheBucket)
    return await cache.delete(url)
  } catch {
    return false
  }
}

// ---------------------------------------------------------------------------
// Phase 0431 D-C6 — BroadcastChannel listener (REMOVED 2026-05-11)
// Phase 0440 Plan 04 RUNTIME-04 — Realtime cutover audit trail
//
// History:
//   The original D-C6 design registered a module-init listener on the
//   "cogo-kg-invalidate" BroadcastChannel that received cross-tab
//   invalidation messages from `index.ts::registerVersionPollingTimer` and
//   called `invalidateShard()` as a documented passthrough until Service
//   Worker cache buckets landed.
//
// The listener has been REMOVED because:
//   1. The cross-tab emitter in `index.ts` was removed in the same commit —
//      with no producer, the listener was dead wiring.
//   2. Phase 0439 REALTIME-01 (shipped at cogo-platform commit `8a4482c1`)
//      supersedes the cross-tab broadcast pattern with a Supabase Realtime
//      subscription on `cogo.application_ontology`. Callers that need cache
//      invalidation now subscribe via
//        `subscribeToTenantOntology` (./realtime-invalidator.ts)
//      or, at the repository layer, via
//        `LocalWasmRepository.subscribeToTenantInvalidation`
//      (@cogo/kg-repository).
//
// `_broadcastChannelForTest` is preserved as a permanent null-returning
// stub so existing test imports compile and the deprecation post-condition
// is observable: callers that load this module and inspect the seam see
// `null`, confirming that no module-init BroadcastChannel listener is
// registered. See `__tests__/shard-cache-listener.test.ts` for the
// deprecation-post-condition assertion.
// ---------------------------------------------------------------------------

/**
 * Test-only — Phase 440 Plan 04 RUNTIME-04 deprecated. Returns `null`
 * permanently. The original Phase 0431 D-C6 BroadcastChannel listener no
 * longer exists; this stub keeps the import surface stable for callers that
 * still reach for the seam (the deprecation-post-condition test in
 * `shard-cache-listener.test.ts` asserts the null return value).
 */
export function _broadcastChannelForTest(): BroadcastChannel | null {
  return null
}
